/**
 * SupplierIntroDialog
 * -------------------
 * Takes an AI-discovered lead from the Sourcing Desk and makes it real: writes
 * the company into the supplier list (optionally as a permanent approved
 * supplier, so they ride along on every future RFQ) and then sends the
 * new-vendor introduction letter — from the portal or as an Outlook draft.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, X, UserPlus, Send, ExternalLink, Mail, ArrowRight, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

export const SupplierIntroDialog = ({ open, onClose, token, lead, onDone }) => {
  const emails = lead?.emails || [];
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState("");
  const [supplier, setSupplier] = useState(null);

  const [f, setF] = useState({
    company_name: lead?.company_name || "",
    contact_name: "",
    email: emails[0] || "",
    phone: (lead?.phones || [])[0] || "",
    website: lead?.source_url || "",
    categories: lead?.sku || "",
    make_permanent: true,
    send_introduction: true,
    include_portal_link: true,
  });

  const [letter, setLetter] = useState({ to: "", cc: "", subject: "", body_html: "" });

  const headers = { Authorization: `Bearer ${token}` };

  if (!open) return null;

  const loadLetter = async (supplierId, includeLink) => {
    const r = await axios.get(`${API}/admin/suppliers/${supplierId}/introduction-letter`, {
      headers, params: { include_portal_link: includeLink },
    });
    setLetter({
      to: (r.data.to || []).join(", "), cc: "",
      subject: r.data.subject || "", body_html: r.data.body_html || "",
    });
  };

  const addSupplier = async () => {
    if (!f.email.trim()) { toast.error("This lead needs an email address first"); return; }
    setBusy("add");
    try {
      const r = await axios.post(`${API}/admin/supplier-leads/${lead.id}/promote`, {
        company_name: f.company_name, contact_name: f.contact_name, email: f.email,
        phone: f.phone, website: f.website,
        categories: f.categories ? f.categories.split(",").map((c) => c.trim()).filter(Boolean) : [],
        make_permanent: f.make_permanent,
      }, { headers });
      setSupplier(r.data.supplier);
      toast.success(r.data.message);
      if (f.send_introduction) {
        await loadLetter(r.data.supplier.id, f.make_permanent && f.include_portal_link);
        setStep("letter");
      } else {
        onDone?.();
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that supplier");
    } finally { setBusy(""); }
  };

  const split = (s) => s.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
  const payload = () => ({
    to: split(letter.to), cc: split(letter.cc), subject: letter.subject,
    body_html: letter.body_html,
    include_portal_link: f.make_permanent && f.include_portal_link,
  });

  const sendLetter = async () => {
    if (!split(letter.to).length) { toast.error("Add at least one recipient"); return; }
    setBusy("send");
    try {
      const r = await axios.post(
        `${API}/admin/suppliers/${supplier.id}/introduction-letter/send`, payload(), { headers });
      if (r.data.success) {
        toast.success(`Introduction sent to ${r.data.sent_to.join(", ")}`);
        onDone?.();
        onClose();
      } else {
        toast.error(r.data.detail || `Mail server returned ${r.data.email_status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the introduction");
    } finally { setBusy(""); }
  };

  const draftLetter = async () => {
    setBusy("outlook");
    try {
      const r = await axios.post(
        `${API}/admin/suppliers/${supplier.id}/introduction-letter/outlook-draft`, payload(), { headers });
      if (r.data.web_link) {
        window.open(r.data.web_link, "_blank", "noopener");
        toast.success("Outlook draft created");
        onDone?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Outlook drafting is unavailable");
      const plain = letter.body_html.replace(/<[^>]+>/g, "").replace(/\n{3,}/g, "\n\n");
      window.location.href = `mailto:${split(letter.to).join(",")}?subject=${encodeURIComponent(letter.subject)}&body=${encodeURIComponent(plain)}`;
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="supplier-intro-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              {step === "form"
                ? <><UserPlus className="w-5 h-5 text-cyan-600" /> Add to the supplier list</>
                : <><Mail className="w-5 h-5 text-cyan-600" /> New-vendor introduction</>}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {step === "form"
                ? "Found by the sourcing crawler — keep them for every future RFQ."
                : `${supplier?.name} · ${supplier?.email}`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        {step === "form" ? (
          <>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Company name">
                  <Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })}
                    className="h-9 text-sm" data-testid="intro-company" />
                </Field>
                <Field label="Contact name (for the greeting)">
                  <Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })}
                    className="h-9 text-sm" placeholder="Sales manager" data-testid="intro-contact" />
                </Field>
                <Field label="Email">
                  {emails.length > 1 ? (
                    <select value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                      className="w-full h-9 text-sm border border-slate-300 rounded-md px-2" data-testid="intro-email-select">
                      {emails.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  ) : (
                    <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                      className="h-9 text-sm" data-testid="intro-email" />
                  )}
                </Field>
                <Field label="Phone">
                  <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-9 text-sm" />
                </Field>
                <Field label="Website">
                  <Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="h-9 text-sm" />
                </Field>
                <Field label="Categories / SKUs they carry">
                  <Input value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })}
                    className="h-9 text-sm" placeholder="comma separated" />
                </Field>
              </div>

              <div className="space-y-2 border border-slate-200 rounded-lg p-3">
                <Check label="Make them a permanent approved supplier"
                  hint="Approved and verified now, so they're included in every future RFQ broadcast."
                  checked={f.make_permanent} testId="intro-permanent"
                  onChange={(v) => setF({ ...f, make_permanent: v })} />
                <Check label="Send the introduction letter"
                  hint="Who we are, what we buy, how our RFQs and POs work, and what we need to set them up."
                  checked={f.send_introduction} testId="intro-send-letter"
                  onChange={(v) => setF({ ...f, send_introduction: v })} />
                {f.send_introduction && f.make_permanent && (
                  <Check label="Include their supplier-portal setup link"
                    hint="Lets them quote online instead of by email."
                    checked={f.include_portal_link} testId="intro-portal-link"
                    onChange={(v) => setF({ ...f, include_portal_link: v })} />
                )}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={addSupplier} disabled={!!busy}
                className="bg-cyan-600 hover:bg-cyan-700" data-testid="intro-add-supplier">
                {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <ArrowRight className="w-4 h-4 mr-1" />}
                {f.send_introduction ? "Add & write the introduction" : "Add to supplier list"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-5 overflow-y-auto space-y-3">
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> On the supplier list
                {f.make_permanent ? " as a permanent approved supplier" : " as a prospect"}
              </p>
              <Row label="To">
                <Input value={letter.to} onChange={(e) => setLetter({ ...letter, to: e.target.value })}
                  className="h-9 text-sm" data-testid="intro-letter-to" />
              </Row>
              <Row label="Cc">
                <Input value={letter.cc} onChange={(e) => setLetter({ ...letter, cc: e.target.value })}
                  className="h-9 text-sm" placeholder="optional" />
              </Row>
              <Row label="Subject">
                <Input value={letter.subject} onChange={(e) => setLetter({ ...letter, subject: e.target.value })}
                  className="h-9 text-sm" data-testid="intro-letter-subject" />
              </Row>
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Preview</p>
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-[40vh] overflow-y-auto text-sm"
                  data-testid="intro-letter-preview" dangerouslySetInnerHTML={{ __html: letter.body_html }} />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Later</Button>
              <Button variant="outline" onClick={draftLetter} disabled={!!busy}
                className="border-cyan-300 text-cyan-700" data-testid="intro-letter-outlook">
                {busy === "outlook" ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <ExternalLink className="w-4 h-4 mr-1" />} Open in Outlook
              </Button>
              <Button onClick={sendLetter} disabled={!!busy}
                className="bg-cyan-600 hover:bg-cyan-700" data-testid="intro-letter-send">
                {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <Send className="w-4 h-4 mr-1" />} Send from portal
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">{label}</span>
    {children}
  </label>
);

const Row = ({ label, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-16 text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</span>
    <div className="flex-1">{children}</div>
  </div>
);

const Check = ({ label, hint, checked, onChange, testId }) => (
  <label className="flex items-start gap-2.5 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 mt-0.5 accent-cyan-600" data-testid={testId} />
    <span>
      <span className="text-sm font-semibold text-slate-900 block">{label}</span>
      <span className="text-[11px] text-slate-500">{hint}</span>
    </span>
  </label>
);

export default SupplierIntroDialog;
